import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
  Typography,
  Divider,
} from "@mui/material";
import axios from "../../../utils/axiosInstance";
import { toast } from "react-toastify";
import { getUIDescription } from "../customerGroupDetails/AssigningCustomerGroupDevice";

const EditRegionDialog = ({
  open,
  onClose,
  technician,
  groups = [],
  group,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    group: "",
    notes: "",
  });

  const [loading, setLoading] = useState(false);

  // Initialize form with technician's current values
  useEffect(() => {
    if (technician) {
      setFormData({
        name: technician?.region || "",
        group: technician?.group || "",
        notes: technician.notes || "",
      });
    }

    getRegionData();
  }, [technician]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getRegionData = async () => {
    if (!technician?.regionId) return;

    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/regions/${technician?.regionId}`
      );

      // Update form data with the fetched notes
      if (response.data) {
        setFormData((prev) => ({
          ...prev,
          notes: response.data.description || "",
        }));
      }
    } catch (error) {
      console.error("Error fetching region data:", error);
      toast.error("Failed to load region details");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTechnician = async () => {
    if (!technician?.id) {
      console.error("No technician selected for update");
      toast.error("No technician selected");
      return;
    }

    // Validation
    if (!formData.name.trim()) {
      toast.error("Region name is required");
      return;
    }

    if (!technician?.regionId) {
      toast.error("Region is required");
      return;
    }

    const payload = {
      groupId: group.id,
      name: formData.name,
      description: formData.notes || "",
      UI_Description: `${getUIDescription()}\n Update Technician (Route)`, 
    };

    setLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_GATEAWAY}/regions/${technician.regionId}`,
        payload
      );

      toast.success("Region updated successfully");

      // Call onSave callback if provided
      if (onSave) {
        onSave(response.data);
      }

      // Close dialog
      handleCancel();
    } catch (error) {
      console.error("Error updating Region:", error);
      toast.error(error.response?.data?.message || "Failed to update Region");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: "",
      group: "",
      notes: "",
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        },
      }}
    >
      <DialogTitle>
        <Typography fontWeight="bold">
          Edit Region "{technician?.region || "Region"}"
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
          {/* Name Field */}
          <Box>
            <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
              Name *
            </Typography>
            <TextField
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0, // ✅ remove rounding
                },
              }}
              fullWidth
              size="small"
              value={formData.name}
              onChange={handleChange("name")}
              placeholder="Enter region name"
              required
            />
          </Box>

          <Divider />

          {/* Group Field */}
          <Box>
            <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
              Group
            </Typography>
            <TextField
             sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0, // ✅ remove rounding
                },
              }}
              select
              fullWidth
              size="small"
              value={formData.group}
              onChange={handleChange("group")}
              disabled
            >
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.name}>
                  {group.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Divider />

          {/* Notes Field */}
          <Box>
            <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
              Notes
            </Typography>
            <TextField
             sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0, // ✅ remove rounding
                },
              }}
              fullWidth
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange("notes")}
              placeholder="Notes about this region..."
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{
            minWidth: 100,
            borderRadius: 0, // ✅ remove rounding
          }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpdateTechnician}
          variant="contained"
          sx={{
            minWidth: 120,
            borderRadius: 0, // ✅ remove rounding
          }}
          disabled={loading}
        >
          {loading ? "Updating..." : "Update Region"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRegionDialog;
